import { PageHeader, Text } from '@lmc/ui-kit'
import styled from 'styled-components'
import { useState } from 'react'
import dayjs from 'dayjs'
import { useQuery } from 'react-query'
import { getJSON } from 'common/services/api'
import DashboardFilters from '../DashboardFilters/DashboardFilters'
import DashboardBody from '../DashboardBody/DashboardBody'
import {
  Home,
  MonitoringReportResponse,
  FluidIntakeTargetsResponse,
} from '@lmc/ts-common'
import { generateApiUrl, generateApiUrlFluidsOverview } from '../../utils'
import { RangeValue } from 'types/shared'
import { Switch } from 'antd'
import { getNominative } from 'common/utils'
import { useSelector } from 'react-redux'
import useFeatureFlag from 'hooks/useFeatureFlags'

export type FilterValues = {
  rangePicker: RangeValue<dayjs.Dayjs>
  residents?: string[]
  riskAssessment?: string | null
  categories?: string[]
  categoryItems?: string[]
  loggedBy?: string[]
  homes?: string[]
  locations?: string[]
  statuses?: string[]
  severities?: string[]
  resultedIn?: string[]
  ownedBy?: string[]
}

const initialFiltersValue: FilterValues = {
  rangePicker: [dayjs().subtract(2, 'week').startOf('day'), dayjs()],
  residents: [],
  loggedBy: [],
}

export type MonitoringReportProps = {
  helpUrl: string
  title: string
  description: string
  reportingId: string
  aggregation: string
}

const MonitoringReport = ({
  helpUrl,
  title,
  description,
  reportingId,
  aggregation,
}: MonitoringReportProps) => {
  const [filterParams, setFilterParams] =
    useState<FilterValues>(initialFiltersValue)

  // Logic for fluids tracking
  const flags = useFeatureFlag({
    flagNames: ['fluid_tracking_monitoring_report'],
  })?.data

  const fluidTrackingMonitoringReportEnabled =
    flags?.fluid_tracking_monitoring_report?.enabled

  const hasFluidMonitoring = reportingId === 'fluids_in'

  const url = generateApiUrl(filterParams, reportingId, aggregation)

  const urlForFluidsOverview = generateApiUrlFluidsOverview(filterParams)

  const [onlyTargets, setOnlyTargets] = useState(false)

  const { data: fluidsData } = useQuery<FluidIntakeTargetsResponse>(
    urlForFluidsOverview,
    () => getJSON({ url: urlForFluidsOverview }),
    { enabled: fluidTrackingMonitoringReportEnabled },
  )

  const fluidsWithTarget = fluidsData || []

  const { isLoading, data: rawData } = useQuery<MonitoringReportResponse>(
    url,
    () => getJSON({ url }),
  )

  // Mapping the fluid tracking target
  const data = {
    ...rawData,
    result: rawData?.result?.map((rawDataItem) => ({
      ...rawDataItem,
      data: rawDataItem.data?.map((rawDateSubItem) => ({
        ...rawDateSubItem,
        target: fluidsWithTarget
          ?.find((x) => x.residentId === rawDataItem?.resident?.id)
          ?.dates?.find((withTargetItem) => {
            return (
              // Matching by Date
              dayjs(withTargetItem.date).format('YYYY-MM-DD') ===
              dayjs(rawDateSubItem?.date, ['DD/MM/YYYY', 'YYYY-MM-DD']).format(
                'YYYY-MM-DD',
              )
            )
          })?.intakeTarget,
      })),
    })),
  }

  // Filtering the only with target
  const getData = () => {
    if (fluidTrackingMonitoringReportEnabled) {
      if (onlyTargets) {
        return {
          ...data,
          result: data?.result?.filter((dataItem) =>
            fluidsWithTarget
              ?.find((x) => x?.residentId === dataItem.resident.id)
              ?.dates?.some((y) => y.intakeTarget !== null),
          ),
        }
      }
      return data
    }

    return data
  }

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || '',
  )

  return (
    <Wrapper>
      <PageHeader
        title={<TitleContainer>{title}</TitleContainer>}
        subtitle={description}
        moreInfoLink={helpUrl}
      />
      <DashboardFilters
        onChange={setFilterParams}
        value={filterParams}
        reportingId={reportingId}
      >
        {fluidTrackingMonitoringReportEnabled && hasFluidMonitoring && (
          <FlexContainer>
            <StyledText>
              Show{' '}
              {getNominative(
                preferredPeopleNominative,
                'clients',
              ).toLowerCase()}{' '}
              with targets
            </StyledText>
            <Switch
              onChange={(value) => setOnlyTargets(value)}
              checked={onlyTargets}
            />
          </FlexContainer>
        )}
      </DashboardFilters>
      <DashboardBody
        // @ts-ignore
        data={getData()}
        reportingId={reportingId}
        filterParams={filterParams}
        isLoading={isLoading}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  max-width: 1480px;
  padding: 24px 0 50px;
  margin: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${({ theme }) => theme.mediaQueries?.tabletDown} {
    padding: 24px;
  }

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin: 0;
  }
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  margin-right: 0px;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

export default MonitoringReport
